import React, { useState, useEffect } from 'react';
import { Heading } from '../Components/Catalyst/heading';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../Components/Catalyst/table';
import { Input, InputGroup } from '../Components/Catalyst/input';
import { Checkbox } from '../Components/Catalyst/checkbox';
import {
  MagnifyingGlassIcon,
  CheckIcon,
  EnvelopeIcon,
  EnvelopeOpenIcon,
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { NotificationResponse } from '../clients/rust-api-client';
import { memberNotificationService } from '../clients/ApiClients';
import { ErrorAlert } from '../Components/Custom/ErrorAlert';
import LoadingSpinner from '../LoadingSpinner';
import { apiErrorMessage } from '../utils/apiErrors';
import { Button } from '../Components/Catalyst/button';

interface NotificationListProps {
  title?: string | null;
}

/**
 * The member's own notifications.
 *
 * Scoped by membership on the server: there is no `memberId` to pass,
 * and the endpoint deliberately returns nothing addressed to staff even
 * within the member's own HOA.
 */
const NotificationList: React.FC<NotificationListProps> = ({ title }) => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<NotificationResponse[]>(
    []
  );
  const [filteredNotifications, setFilteredNotifications] = useState<
    NotificationResponse[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const [selectedNotifications, setSelectedNotifications] = useState<
    Set<string>
  >(new Set());
  const [fetchError, setFetchError] = useState<string | null>(null);
  // Bumped by "Try again" to re-run the fetch below.
  const [reloadKey, setReloadKey] = useState(0);
  // A read/unread change that the server refused. These used to fail
  // with only a console line, leaving the envelope showing a state the
  // server never recorded.
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        setLoading(true);
        setFetchError(null);
        const response = await memberNotificationService.listMyNotifications({
          unreadOnly: filter === 'unread',
          perPage: 100,
        });

        setNotifications(response.data.notifications);
        setFilteredNotifications(response.data.notifications);
      } catch (err) {
        console.error('Error fetching notifications:', err);
        setFetchError(apiErrorMessage(err, "Couldn't load notifications."));
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
  }, [filter, reloadKey]);

  useEffect(() => {
    const filtered = notifications.filter(
      (notification) =>
        notification.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        notification.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
        notification.notification_type
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
    );
    setFilteredNotifications(filtered);
  }, [searchTerm, notifications]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleRowClick = (notification: NotificationResponse) => {
    navigate(`/notification/${notification.id}`);
  };

  const handleCheckboxClick = (checked: boolean, notificationId: string) => {
    const newSelected = new Set(selectedNotifications);
    if (!checked) {
      newSelected.delete(notificationId);
    } else {
      newSelected.add(notificationId);
    }
    setSelectedNotifications(newSelected);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = new Set(
        filteredNotifications.map((notification) => notification.id)
      );
      setSelectedNotifications(allIds);
    } else {
      setSelectedNotifications(new Set());
    }
  };

  const toggleReadStatus = async (
    notification: NotificationResponse,
    event: React.MouseEvent
  ) => {
    event.stopPropagation(); // Prevent row click
    const nextIsRead = !notification.is_read;
    try {
      if (nextIsRead) {
        await memberNotificationService.markMyNotificationRead({
          notificationId: notification.id,
        });
      } else {
        await memberNotificationService.markMyNotificationUnread({
          notificationId: notification.id,
        });
      }

      // Update local state
      const apply = (prev: NotificationResponse[]) =>
        prev.map((n) =>
          n.id === notification.id ? { ...n, is_read: nextIsRead } : n
        );
      setNotifications(apply);
      setFilteredNotifications(apply);
    } catch (err) {
      console.error('Failed to update notification read status:', err);
      setActionError(
        apiErrorMessage(
          err,
          nextIsRead
            ? "Couldn't mark that notification as read."
            : "Couldn't mark that notification as unread."
        )
      );
    }
  };

  const bulkUpdateReadStatus = async () => {
    if (selectedNotifications.size === 0) return;

    const selectedNotificationsList = filteredNotifications.filter((n) =>
      selectedNotifications.has(n.id)
    );

    // Gmail-like logic: if any selected are unread, mark all as read
    // If all selected are read, mark all as unread
    const hasUnread = selectedNotificationsList.some(
      (n) => !(n.is_read ?? false)
    );
    const newReadStatus = hasUnread;

    try {
      const response = await memberNotificationService.bulkMarkMyNotifications({
        bulkReadStatusUpdate: {
          notification_ids: Array.from(selectedNotifications),
          is_read: newReadStatus,
        },
      });

      // Update local state based on response
      setNotifications((prev) =>
        prev.map((notification) => {
          const updated = response.data.updated_notifications.find(
            (u) => u.id === notification.id
          );
          return updated ? updated : notification;
        })
      );
      setFilteredNotifications((prev) =>
        prev.map((notification) => {
          const updated = response.data.updated_notifications.find(
            (u) => u.id === notification.id
          );
          return updated ? updated : notification;
        })
      );

      // Clear selection
      setSelectedNotifications(new Set());
    } catch (err) {
      console.error('Failed to bulk update notifications:', err);
      setActionError(
        apiErrorMessage(err, "Couldn't update those notifications.")
      );
    }
  };

  const markAllAsRead = async () => {
    const unreadNotifications = notifications.filter(
      (n) => !(n.is_read ?? false)
    );

    if (unreadNotifications.length === 0) return;

    try {
      // Use bulk endpoint for better performance
      await memberNotificationService.bulkMarkMyNotifications({
        bulkReadStatusUpdate: {
          notification_ids: unreadNotifications.map((n) => n.id),
          is_read: true,
        },
      });

      // Update local state
      setNotifications((prev) =>
        prev.map((notification) => ({ ...notification, is_read: true }))
      );
      setFilteredNotifications((prev) =>
        prev.map((notification) => ({ ...notification, is_read: true }))
      );
    } catch (err) {
      console.error('Failed to mark all notifications as read:', err);
      setActionError(
        apiErrorMessage(err, "Couldn't mark everything as read.")
      );
    }
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const getTypeColor = (type: string) => {
    switch (type.toLowerCase()) {
      case 'violation':
        return 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100';
      case 'info':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-100';
      case 'success':
        return 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100';
      case 'warning':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-100';
      default:
        return 'bg-zinc-100 text-zinc-800 dark:bg-zinc-800 dark:text-zinc-100';
    }
  };

  const getReadIcon = (isRead: boolean) => {
    return isRead ? (
      <EnvelopeOpenIcon className="h-5 w-5 text-gray-400" />
    ) : (
      <EnvelopeIcon className="h-5 w-5 text-sky-600" />
    );
  };

  const allSelected =
    filteredNotifications.length > 0 &&
    filteredNotifications.every((notification) =>
      selectedNotifications.has(notification.id)
    );
  const someSelected = filteredNotifications.some((notification) =>
    selectedNotifications.has(notification.id)
  );

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <div className="flex flex-col space-y-4 mb-4">
        <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
          <Heading level={4}>{title || 'Your Notifications'}</Heading>
          <div className="w-full sm:w-64 bg-stone-300 dark:bg-gray-800">
            <InputGroup className="[&>[data-slot=icon]]:text-zinc-500 dark:[&>[data-slot=icon]]:text-white">
              <MagnifyingGlassIcon data-slot="icon" />
              <Input
                type="search"
                placeholder="Search notifications..."
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </InputGroup>
          </div>
        </div>

        {/* Filter buttons and bulk actions */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex gap-2">
            <Button
              onClick={() => setFilter('all')}
              color={filter === 'all' ? 'sky' : 'zinc'}
              aria-pressed={filter === 'all'}
            >
              All
            </Button>
            <Button
              onClick={() => setFilter('unread')}
              color={filter === 'unread' ? 'sky' : 'zinc'}
              aria-pressed={filter === 'unread'}
            >
              Unread
            </Button>
          </div>

          <div className="flex gap-2">
            {selectedNotifications.size > 0 && (
              <Button onClick={bulkUpdateReadStatus} color="sky">
                <CheckIcon className="h-4 w-4" />
                Toggle Read Status
              </Button>
            )}
            {notifications.some((n) => !(n.is_read ?? false)) && (
              <Button onClick={markAllAsRead} color="zinc">
                <CheckIcon className="h-4 w-4" />
                Mark All Read
              </Button>
            )}
          </div>
        </div>
      </div>

      <ErrorAlert
        className="mb-4"
        onRetry={fetchError ? () => setReloadKey((k) => k + 1) : undefined}
        onDismiss={() => setFetchError(null)}
      >
        {fetchError}
      </ErrorAlert>
      <ErrorAlert className="mb-4" onDismiss={() => setActionError(null)}>
        {actionError}
      </ErrorAlert>

      <div className="bg-stone-100 dark:bg-gray-600 rounded-lg shadow transition-colors duration-200 overflow-hidden">
        <div className="px-4 py-5 sm:p-6">
          <div className="overflow-x-auto">
            <div className="inline-block min-w-full align-middle">
              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                <div className="max-h-[500px] overflow-y-auto">
                  <Table>
                    <TableHead className="sticky top-0 z-10 bg-stone-100 dark:bg-gray-600">
                      <TableRow>
                        <TableHeader className="w-12">
                          <Checkbox
                            checked={allSelected}
                            indeterminate={!allSelected && someSelected}
                            onChange={handleSelectAll}
                            color="sky"
                          />
                        </TableHeader>
                        <TableHeader className="text-base font-semibold">
                          Status
                        </TableHeader>
                        <TableHeader className="text-base font-semibold">
                          Type
                        </TableHeader>
                        <TableHeader className="text-base font-semibold">
                          Title
                        </TableHeader>
                        <TableHeader className="text-base font-semibold">
                          Message
                        </TableHeader>
                        <TableHeader className="text-base font-semibold">
                          Date
                        </TableHeader>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredNotifications.map((notification) => (
                        <TableRow
                          key={notification.id}
                          onClick={() => handleRowClick(notification)}
                          className={`cursor-pointer hover:bg-stone-200 dark:hover:bg-zinc-700 transition-colors duration-150 ${
                            !(notification.is_read ?? false)
                              ? 'bg-sky-50/50 dark:bg-sky-900/10'
                              : ''
                          }`}
                        >
                          <TableCell
                            className="w-12"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Checkbox
                              checked={selectedNotifications.has(
                                notification.id
                              )}
                              onChange={(checked) =>
                                handleCheckboxClick(checked, notification.id)
                              }
                              color="sky"
                            />
                          </TableCell>
                          <TableCell>
                            <button
                              type="button"
                              onClick={(e) => toggleReadStatus(notification, e)}
                              aria-label={
                                notification.is_read
                                  ? 'Mark as unread'
                                  : 'Mark as read'
                              }
                              title={
                                notification.is_read
                                  ? 'Mark as unread'
                                  : 'Mark as read'
                              }
                            >
                              {getReadIcon(notification.is_read ?? false)}
                            </button>
                          </TableCell>
                          <TableCell>
                            <span
                              className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getTypeColor(
                                notification.notification_type
                              )}`}
                            >
                              {notification.notification_type}
                            </span>
                          </TableCell>
                          <TableCell className="font-medium">
                            {notification.title}
                          </TableCell>
                          <TableCell className="max-w-xs truncate">
                            {notification.message}
                          </TableCell>
                          <TableCell>
                            {formatDate(notification.created_at)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default NotificationList;
